import React, { useEffect, useState } from 'react';
import apiService from '../services/api';

export interface OpzioneDropdown {
  value: string;
  label: string;
}

export type OpzioniDropdown = Record<string, OpzioneDropdown[]>;

const uguali = (valori: string[]): OpzioneDropdown[] => valori.map((v) => ({ value: v, label: v }));

// Opzioni usate se il server non risponde: le stesse presenti nel database all'avvio.
export const OPZIONI_DEFAULT: OpzioniDropdown = {
  specie_paziente: uguali(['Cane', 'Gatto', 'Coniglio', 'Uccello', 'Altro']),
  sesso_paziente: uguali(['Maschio', 'Femmina', 'Maschio castrato', 'Femmina sterilizzata']),
  frequenza_cardiaca: [
    ...uguali(['60', '80', '100', '120', '140', '160', '180']),
    { value: '181', label: '> 180' },
  ],
  ritmo_cardiaco: [
    { value: 'regolare', label: 'Regolare' },
    { value: 'irregolare', label: 'Irregolare' },
  ],
  mucose: [
    { value: 'nella_norma', label: 'Nella norma' },
    { value: 'cianotiche', label: 'Cianotiche' },
    { value: 'pallide', label: 'Pallide' },
    { value: 'congeste', label: 'Congeste' },
    { value: 'subitteriche', label: 'Subitteriche' },
    { value: 'pigmentate', label: 'Pigmentate' },
    { value: 'polso', label: 'Polso di Quinc' },
    { value: 'itteriche', label: 'Itteriche' },
  ],
  trc: [
    { value: '1', label: '< 2 s' },
    { value: '2', label: '= 2 s' },
    { value: '3', label: '> 2 s' },
    { value: 'aumentato', label: 'Aumentato' },
    { value: 'diminuito', label: 'Diminuito' },
    { value: 'normale', label: 'Normale' },
  ],
  itto_cardiaco: [
    { value: 'conservato', label: 'Conservato' },
    { value: 'aumentato', label: 'Aumentato' },
    { value: 'diminuito', label: 'Diminuito' },
  ],
  polso_femorale: [
    { value: 'nella_norma', label: 'Nella norma' },
    { value: 'alterato', label: 'Alternante' },
    { value: 'assente', label: 'Assente' },
    { value: 'debole', label: 'Debole' },
    { value: 'paradosso', label: 'Paradosso' },
    { value: 'piccolo', label: 'Piccolo' },
    { value: 'duro', label: 'Duro' },
    { value: 'martellante', label: 'Martellante' },
  ],
  polso_giugulare: [
    { value: 'presente', label: 'Presente' },
    { value: 'assente', label: 'Assente' },
    { value: 'turgore', label: 'Turgore delle giugulari' },
  ],
  attivita: [
    { value: 'diminuita', label: 'Diminuita' },
    { value: 'conservata', label: 'Conservata' },
    { value: 'nv', label: 'Non Valutabile' },
  ],
  tosse: [
    { value: 'assente', label: 'Assente' },
    { value: 'episodi', label: 'Episodi' },
    { value: 'frequente', label: 'Frequente' },
  ],
};

/** Carica dal server le opzioni dei menu a tendina (gestite dal pannello admin). */
export const useOpzioniDropdown = (): OpzioniDropdown => {
  const [opzioni, setOpzioni] = useState<OpzioniDropdown>(OPZIONI_DEFAULT);

  useEffect(() => {
    let attivo = true;
    apiService
      .getOpzioniDropdown()
      .then((data) => {
        if (attivo && data) setOpzioni({ ...OPZIONI_DEFAULT, ...data });
      })
      .catch(() => {
        // resta valido l'elenco di riserva
      });
    return () => {
      attivo = false;
    };
  }, []);

  return opzioni;
};

interface OpzioniOptionsProps {
  campo: string;
  opzioni: OpzioniDropdown;
  current?: string;
}

/** Le <option> di un menu. Se il valore già salvato non è più in elenco (opzione disattivata) resta selezionabile. */
export const OpzioniOptions: React.FC<OpzioniOptionsProps> = ({ campo, opzioni, current }) => {
  const lista = opzioni[campo] || [];
  const mancante = !!current && !lista.some((o) => o.value === current);
  return (
    <>
      {lista.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {mancante && <option value={current}>{current}</option>}
    </>
  );
};
